// ** React Imports
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ** Config
import { i18nInitOptions } from "config/i18n";
import config from "configuration";

// #####################################################

/**
 * Hook do generowania tagów hreflang dla wersji językowych strony
 * Generuje tagi <link rel="alternate" hreflang="..." href="..." />
 * oraz tag x-default dla głównej wersji polskiej
 */
const useHrefLangTags = () => {
	const location = useLocation();
	const { i18n } = useTranslation();

	const currentLanguage = i18n.language;
	const supportedLanguages = i18nInitOptions.supportedLngs || [
		"pl",
		"en",
		"de",
		"cs",
		"ru",
		"uk",
	];
	const fallbackLanguage = i18nInitOptions.fallbackLng || "pl";

	// Odfiltruj nieprawidłowe/deweloperskie kody języków
	const validSupportedLanguages = supportedLanguages.filter((lang) => {
		// Odrzuć cimode i inne deweloperskie/specjalne tryby
		return (
			lang !== "cimode" &&
			lang !== "dev" &&
			/^[a-z]{2}(-[A-Z]{2})?$/.test(lang)
		);
	});

	const hrefLangTags = useMemo(() => {
		const currentPath = location.pathname;

		const protocol = process.env.RAZZLE_PROTOCOL || "https";
		const mainDomain =
			process.env.RAZZLE_FRONT_HOST || config.host || "www.datezone.com";

		// Usuń subdomenę językową z domeny głównej jeśli istnieje
		const baseDomain = mainDomain.replace(/^(www|pl|en|de|cs|ru|uk)\./, "");
		const isMainDomainSubdomain =
			mainDomain.startsWith("www.") ||
			validSupportedLanguages.some((lang) =>
				mainDomain.startsWith(`${lang}.`),
			);
		const finalBaseDomain = isMainDomainSubdomain ? baseDomain : mainDomain;

		const tags = [];

		// Generuj tagi hreflang dla każdego obsługiwanego języka
		validSupportedLanguages.forEach((lang) => {
			let hrefLangUrl;

			if (lang === "pl") {
				// Polski (główny) używa www
				hrefLangUrl = `${protocol}://www.${finalBaseDomain}${currentPath}`;
			} else {
				// Inne języki używają subdomen językowych
				hrefLangUrl = `${protocol}://${lang}.${finalBaseDomain}${currentPath}`;
			}

			// Usuń końcowy "/" dla strony głównej
			if (currentPath === "/") {
				hrefLangUrl = hrefLangUrl.slice(0, -1);
			}

			tags.push({
				rel: "alternate",
				hrefLang: lang,
				href: hrefLangUrl,
			});
		});

		// Dodaj tag x-default wskazujący na główną wersję polską
		let xDefaultUrl = `${protocol}://www.${finalBaseDomain}${currentPath}`;
		// Usuń końcowy "/" dla strony głównej w x-default
		if (currentPath === "/") {
			xDefaultUrl = xDefaultUrl.slice(0, -1);
		}

		tags.push({
			rel: "alternate",
			hrefLang: "x-default",
			href: xDefaultUrl,
		});

		return tags;
	}, [
		location.pathname,
		location.search,
		currentLanguage,
		validSupportedLanguages,
		fallbackLanguage,
	]);

	// Zwróć JSX elements gotowe do użycia w <Helmet>
	const hrefLangElements = useMemo(() => {
		return hrefLangTags.map((tag) => ({
			...tag,
			key: `hreflang-${tag.hrefLang}`,
		}));
	}, [hrefLangTags]);

	return hrefLangElements;
};

// #####################################################

export default useHrefLangTags;
